"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateRatePlan = exports.getPlanBlendedRate = exports.computeBlendedRate = exports.simulateYear = exports.computeCost = exports.getMarginalRate = exports.resolvePeriodIndex = exports.parseDateHour = exports.DEFAULT_MONTHLY_KWH = void 0;
const types_1 = require("./types");
/**
 * Monthly consumption assumed when blending a tariff down to a single rate.
 *
 * Roughly a typical SCE residential month. It matters only for tiered plans, where it decides how
 * much energy is priced above the baseline allowance — and it is an assumption, not a measurement,
 * which is why `computeBlendedRate` accepts an override and why a household's own usage should be
 * used once there is enough of it.
 */
exports.DEFAULT_MONTHLY_KWH = 500;
/** `YYYY-MM-DD-HH`, the `SensorUsageRollup.dateHour` format. */
const DATE_HOUR_PATTERN = /^(\d{4})-(\d{2})-(\d{2})-(\d{2})$/;
/**
 * Parses a rollup's `dateHour` key. Returns null for anything malformed so a bad row is skipped
 * rather than silently priced as some other hour.
 */
const parseDateHour = (dateHour) => {
    const match = DATE_HOUR_PATTERN.exec(dateHour);
    if (!match)
        return null;
    const [, year, month, day, hour] = match;
    const parsed = {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
    };
    const validMonth = parsed.month >= 1 && parsed.month <= 12;
    const validDay = parsed.day >= 1 && parsed.day <= 31;
    const validHour = parsed.hour >= 0 && parsed.hour <= 23;
    return validMonth && validDay && validHour ? parsed : null;
};
exports.parseDateHour = parseDateHour;
const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
/**
 * Whether a local date falls on a weekend.
 *
 * Built from the calendar parts through `Date.UTC` deliberately: the day of week of a given
 * calendar date is the same everywhere, so keeping the arithmetic in UTC avoids the host
 * timezone shifting the date underneath us.
 */
const isWeekend = ({ year, month, day }) => {
    const dayOfWeek = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
};
/** Period index a plan prices a given hour under. */
const resolvePeriodIndex = (plan, at) => {
    const grid = isWeekend(at) ? plan.weekendGrid : plan.weekdayGrid;
    const row = grid[at.month - 1];
    const digit = row?.[at.hour];
    const index = digit === undefined ? NaN : Number(digit);
    // A grid that indexes past the plan's periods means the committed data is inconsistent.
    // `validateRatePlan` catches that in tests; at runtime, clamping keeps a dashboard rendering
    // rather than throwing inside a render pass.
    const periods = plan.periodsByVariant[plan.defaultVariantId] ?? [];
    if (!Number.isFinite(index))
        return 0;
    return Math.min(index, Math.max(periods.length - 1, 0));
};
exports.resolvePeriodIndex = resolvePeriodIndex;
const periodsFor = (plan, variantId) => {
    const requested = variantId ?? plan.defaultVariantId;
    return (plan.periodsByVariant[requested] ??
        plan.periodsByVariant[plan.defaultVariantId] ??
        plan.periodsByVariant[types_1.DEFAULT_VARIANT] ??
        []);
};
/**
 * Price of the next kWh in a given hour.
 *
 * For a tiered plan this depends on how much has already been used in the cycle, so omitting
 * `cycle` prices the hour as though the cycle had just begun — the cheapest tier, which
 * understates most households' marginal cost. Callers pricing real consumption should pass it,
 * or use `computeCost`, which tracks it for them.
 */
const getMarginalRate = (plan, at, variantId, cycle) => {
    const periods = periodsFor(plan, variantId);
    const period = periods[(0, exports.resolvePeriodIndex)(plan, at)];
    const tiers = period?.tiers ?? [];
    if (tiers.length === 0)
        return 0;
    const days = cycle?.days ?? daysInMonth(at.year, at.month);
    const consumed = cycle?.kwhSoFar ?? 0;
    for (const tier of tiers) {
        const limit = tier.maxKwhPerDay === null ? Infinity : tier.maxKwhPerDay * days;
        if (consumed < limit)
            return tier.rate + tier.adj;
    }
    const last = tiers[tiers.length - 1];
    return last.rate + last.adj;
};
exports.getMarginalRate = getMarginalRate;
/**
 * Charges one hour's energy, splitting it across tier boundaries when it straddles one.
 * Returns the cost and the cycle total after this hour.
 */
const chargeHour = (tiers, kwh, consumedBefore, days) => {
    let remaining = kwh;
    let consumed = consumedBefore;
    let cost = 0;
    for (const tier of tiers) {
        if (remaining <= 0)
            break;
        const limit = tier.maxKwhPerDay === null ? Infinity : tier.maxKwhPerDay * days;
        if (consumed >= limit)
            continue;
        const take = Math.min(remaining, limit - consumed);
        cost += take * (tier.rate + tier.adj);
        consumed += take;
        remaining -= take;
    }
    // Only reachable if the top tier is bounded, which no well-formed plan should be.
    if (remaining > 0 && tiers.length > 0) {
        const last = tiers[tiers.length - 1];
        cost += remaining * (last.rate + last.adj);
        consumed += remaining;
    }
    return { cost, consumedAfter: consumed };
};
const cycleKeyOf = ({ year, month }) => `${year}-${month}`;
const dayKeyOf = ({ year, month, day }) => `${year}-${month}-${day}`;
/**
 * Costs a series of hourly readings under one plan.
 *
 * Hours are sorted internally, so callers may pass rollups in whatever order the query returned
 * them. Cumulative tier position is tracked per calendar month; fixed charges accrue once per
 * distinct day present in the data, meaning a partial month is charged only for the days it
 * actually covers rather than a whole month's standing charge.
 */
const computeCost = (plan, hours, variantId) => {
    const periods = periodsFor(plan, variantId);
    const ordered = [...hours].sort((a, b) => {
        const aKey = a.at.year * 1e6 + a.at.month * 1e4 + a.at.day * 100 + a.at.hour;
        const bKey = b.at.year * 1e6 + b.at.month * 1e4 + b.at.day * 100 + b.at.hour;
        return aKey - bKey;
    });
    const consumedByCycle = new Map();
    const energyByDay = new Map();
    let energyCost = 0;
    let totalKwh = 0;
    for (const { at, kwh } of ordered) {
        if (!Number.isFinite(kwh) || kwh <= 0)
            continue;
        const period = periods[(0, exports.resolvePeriodIndex)(plan, at)];
        const tiers = period?.tiers ?? [];
        if (tiers.length === 0)
            continue;
        const cycleKey = cycleKeyOf(at);
        const consumedBefore = consumedByCycle.get(cycleKey) ?? 0;
        const { cost, consumedAfter } = chargeHour(tiers, kwh, consumedBefore, daysInMonth(at.year, at.month));
        consumedByCycle.set(cycleKey, consumedAfter);
        const dayKey = dayKeyOf(at);
        energyByDay.set(dayKey, (energyByDay.get(dayKey) ?? 0) + cost);
        energyCost += cost;
        totalKwh += kwh;
    }
    // A daily minimum is a floor on the whole day's bill, so it is compared against energy plus
    // the standing charge — not added on top of them.
    let fixedCost = 0;
    for (const dayEnergy of energyByDay.values()) {
        const withStanding = dayEnergy + plan.fixedChargePerDay;
        const floored = Math.max(withStanding, plan.minChargePerDay ?? 0);
        fixedCost += floored - dayEnergy;
    }
    return {
        energyCost,
        fixedCost,
        totalCost: energyCost + fixedCost,
        kwh: totalKwh,
        effectiveRatePerKwh: totalKwh > 0 ? energyCost / totalKwh : 0,
    };
};
exports.computeCost = computeCost;
/**
 * Simulates a full year of flat hourly consumption to expose a plan's whole-year cost.
 *
 * Flat consumption is a deliberate simplification: a real household's shape is peakier than this,
 * and on a TOU plan the shape is exactly what decides the bill. So this is a fair basis for
 * comparing plans against each other, and a rough one for predicting an individual bill.
 */
const simulateYear = (plan, options = {}) => {
    const { monthlyKwh = exports.DEFAULT_MONTHLY_KWH, year = 2026, variantId } = options;
    const hours = [];
    for (let month = 1; month <= 12; month += 1) {
        const days = daysInMonth(year, month);
        const kwhPerHour = monthlyKwh / (days * 24);
        for (let day = 1; day <= days; day += 1) {
            for (let hour = 0; hour < 24; hour += 1) {
                hours.push({ at: { year, month, day, hour }, kwh: kwhPerHour });
            }
        }
    }
    return (0, exports.computeCost)(plan, hours, variantId);
};
exports.simulateYear = simulateYear;
/**
 * A plan blended to one $/kWh, for seeding the flat `rate_per_kWh` that existing cost surfaces
 * read. Energy only — fixed charges are returned separately by {@link simulateYear} because they
 * do not scale with consumption, and folding them in would misprice a household whose usage
 * differs from the assumption.
 */
const computeBlendedRate = (plan, options = {}) => (0, exports.simulateYear)(plan, options).effectiveRatePerKwh;
exports.computeBlendedRate = computeBlendedRate;
/**
 * Blended rates memoised across calls.
 *
 * Safe to cache indefinitely: the inputs are committed table data and a handful of numbers, so
 * the result cannot change within a process. Worth caching because a blend simulates all 8,760
 * hours of a year, which is far too slow to repeat inside a React render — a picker showing four
 * plans would otherwise stall on every keystroke.
 */
const blendedRateCache = new Map();
const getPlanBlendedRate = (plan, options = {}) => {
    const { variantId = plan.defaultVariantId, monthlyKwh = exports.DEFAULT_MONTHLY_KWH, year = 2026, } = options;
    const key = `${plan.id}|${variantId}|${monthlyKwh}|${year}`;
    const cached = blendedRateCache.get(key);
    if (cached !== undefined)
        return cached;
    const rate = (0, exports.computeBlendedRate)(plan, { variantId, monthlyKwh, year });
    blendedRateCache.set(key, rate);
    return rate;
};
exports.getPlanBlendedRate = getPlanBlendedRate;
/**
 * Structural checks on a committed plan. Exercised by the test suite so a bad refresh of
 * `ratePlans.ts` fails in CI rather than mispricing a customer's dashboard.
 */
const validateRatePlan = (plan) => {
    const problems = [];
    const variantIds = Object.keys(plan.periodsByVariant);
    if (variantIds.length === 0)
        problems.push(`${plan.id}: no variants`);
    if (!plan.periodsByVariant[plan.defaultVariantId]) {
        problems.push(`${plan.id}: defaultVariantId '${plan.defaultVariantId}' is not a variant`);
    }
    for (const [name, grid] of [
        ['weekdayGrid', plan.weekdayGrid],
        ['weekendGrid', plan.weekendGrid],
    ]) {
        if (grid.length !== 12)
            problems.push(`${plan.id}: ${name} has ${grid.length} months, want 12`);
        grid.forEach((row, monthIndex) => {
            if (row.length !== 24) {
                problems.push(`${plan.id}: ${name}[${monthIndex}] has ${row.length} hours, want 24`);
            }
            if (!/^\d{24}$/.test(row)) {
                problems.push(`${plan.id}: ${name}[${monthIndex}] is not 24 digits`);
            }
        });
    }
    for (const variantId of variantIds) {
        const periods = plan.periodsByVariant[variantId];
        const maxIndex = Math.max(...plan.weekdayGrid.concat(plan.weekendGrid).flatMap((row) => row.split('').map(Number)));
        if (maxIndex >= periods.length) {
            problems.push(`${plan.id}/${variantId}: grid references period ${maxIndex} but only ${periods.length} exist`);
        }
        periods.forEach((period, periodIndex) => {
            if (period.tiers.length === 0) {
                problems.push(`${plan.id}/${variantId}: period ${periodIndex} has no tiers`);
            }
            // A bounded top tier would leave consumption above it unpriced.
            const top = period.tiers[period.tiers.length - 1];
            if (top && top.maxKwhPerDay !== null) {
                problems.push(`${plan.id}/${variantId}: period ${periodIndex} top tier is bounded`);
            }
            // Allowances must climb, or the tier walk would skip steps.
            period.tiers.slice(0, -1).forEach((tier, tierIndex) => {
                const next = period.tiers[tierIndex + 1];
                if (tier.maxKwhPerDay !== null &&
                    next.maxKwhPerDay !== null &&
                    next.maxKwhPerDay <= tier.maxKwhPerDay) {
                    problems.push(`${plan.id}/${variantId}: period ${periodIndex} tier ${tierIndex + 1} allowance does not increase`);
                }
            });
        });
        if (plan.tiered && periods.every((period) => period.tiers.length === 1)) {
            problems.push(`${plan.id}/${variantId}: marked tiered but no period has multiple tiers`);
        }
    }
    return problems;
};
exports.validateRatePlan = validateRatePlan;
